import _ from 'lodash';
import {connect} from 'react-redux';

import {CrmSyncIntegrationResourceTypesEnum} from '../../../../../../../../../src/models/api/crmSyncModel';
import {
  CustomFieldModel,
  CustomFieldResourceTypesEnum,
} from '../../../../../../../../../src/models/api/customFieldModel';
import {AppState} from '../../../../../../../../../src/store/appState';
import {getFlatCustomFields} from '../../../../../../../../../src/store/data/dataSelectors';
import {
  createSelector,
  makeMapStateToProps,
} from '../../../../../../../../../srcCommon/helpers/reselectHelpers';
import {
  computeCrmSyncDefaultCustomFields,
  computeCrmSyncDefaultFieldType,
  computeMultiAddFieldIds,
} from '../crmSyncsPageConfig/crmSyncsPageConfigHelpers';
import {FrontFieldDropdownWrapper} from './frontFieldDropdownWrapper';

/*
 * Interface.
 */

interface FrontFieldDropdownContainerProps {
  types: ReadonlyArray<string>;
  resourceType: CrmSyncIntegrationResourceTypesEnum;
  selectedFieldId?: number;
  allSelectedFieldIds: ReadonlyArray<number>;
}

/*
 * Selectors.
 */

function getAllowedTypes(_state: AppState, props: FrontFieldDropdownContainerProps) {
  return props.types;
}

function getSelectedFieldId(_state: AppState, props: FrontFieldDropdownContainerProps) {
  return props.selectedFieldId;
}

function getAllSelectedFieldIds(_state: AppState, props: FrontFieldDropdownContainerProps) {
  return props.allSelectedFieldIds;
}

function getResourceType(_state: AppState, props: FrontFieldDropdownContainerProps) {
  return props.resourceType;
}

// this function returns both default and customfields as CustomFieldModel type
function makeGetCustomFields() {
  return createSelector(
    [getFlatCustomFields, getAllowedTypes, getSelectedFieldId, getAllSelectedFieldIds, getResourceType],
    (customFields, allowedTypes, selectedFieldId, allSelectedFieldIds, resourceType) =>
      _([...computeCrmSyncDefaultCustomFields(resourceType), ...customFields])
        .filter((customField) =>
          filterCustomField(resourceType, customField, allowedTypes, allSelectedFieldIds, selectedFieldId),
        )
        .sort((field) => (field.id === selectedFieldId ? -1 : 1))
        .value(),
  );
}

/*
 * Helpers.
 */

function filterCustomField(
  resourceType: CrmSyncIntegrationResourceTypesEnum,
  customField: CustomFieldModel,
  allowedTypes: ReadonlyArray<string>,
  allSelectedFieldIds: ReadonlyArray<number>,
  selectedFieldId?: number,
) {
  const customFieldResourceType =
    resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT
      ? CustomFieldResourceTypesEnum.ACCOUNT
      : CustomFieldResourceTypesEnum.CARD;

  // Only allow the custom fields for the specified resource.
  if (customField.resourceType !== customFieldResourceType) {
    return false;
  }

  // default resource fields have negative ids and special types
  const fieldType =
    customField.id > 0 ? customField.type : computeCrmSyncDefaultFieldType(customField.id, resourceType);

  // Make sure field type is allowed.
  if (!fieldType || !allowedTypes.includes(fieldType)) {
    return false;
  }

  // If all the typings are valid, only show the selected field and fields that are not selected by other mappings,
  // or special fields that can be added multiple times
  return (
    customField.id === selectedFieldId ||
    !allSelectedFieldIds.includes(customField.id) ||
    computeMultiAddFieldIds(resourceType).includes(customField.id)
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<FrontFieldDropdownContainerProps>()(() => ({
  customFields: makeGetCustomFields(),
}));

export const FrontFieldDropdownContainer = connect(mapStateToProps)(FrontFieldDropdownWrapper);
